import {
  Directive,
  HostListener
} from '@angular/core';


@Directive({
  selector: '[appPhoneNumberValidation]',
  standalone: true
})
export class PhoneNumberValidationDirective {

  // =========================================================
  // KEY DOWN
  // =========================================================

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {

    const input =
      event.target as HTMLInputElement;

    const allowedKeys = [
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End'
    ];

    if (
      allowedKeys.includes(event.key)
    ) {
      return;
    }

    if (
      event.ctrlKey ||
      event.metaKey
    ) {
      return;
    }


    // =====================================================
    // BACKSPACE
    // =====================================================

    if (
      event.key === 'Backspace'
    ) {
      event.preventDefault();

      let digits =
        this.getDigits(
          input.value
        );

      if (
        digits.length > 0
      ) {
        digits =
          digits.substring(
            0,
            digits.length - 1
          );
      }

      this.updateInput(
        input,
        digits
      );

      return;
    }


    // =====================================================
    // DELETE
    // =====================================================

    if (
      event.key === 'Delete'
    ) {
      event.preventDefault();

      let digits =
        this.getDigits(
          input.value
        );

      if (
        digits.length > 0
      ) {
        digits =
          digits.substring(
            0,
            digits.length - 1
          );
      }

      this.updateInput(
        input,
        digits
      );

      return;
    }


    // =====================================================
    // NUMBERS ONLY
    // =====================================================

    if (
      !/^[0-9]$/.test(
        event.key
      )
    ) {
      event.preventDefault();
      return;
    }


    const digits =
      this.getDigits(
        input.value
      );


    // =====================================================
    // FIRST DIGIT MUST BE 0 OR 1
    // =====================================================

    if (
      digits.length === 0 &&
      event.key !== '0' &&
      event.key !== '1'
    ) {
      event.preventDefault();
      return;
    }


    // =====================================================
    // MAXIMUM 15 ACTUAL DIGITS
    // =====================================================

    if (
      digits.length >= 15
    ) {
      event.preventDefault();
      return;
    }
  }


  // =========================================================
  // INPUT
  // Handles paste + mobile input
  // =========================================================

  @HostListener('input', ['$event'])
  onInput(event: Event): void {

    const input =
      event.target as HTMLInputElement;

    let digits =
      this.getDigits(
        input.value
      );


    // Must start with 0 or 1
    if (
      digits.length > 0 &&
      !digits.startsWith('0') &&
      !digits.startsWith('1')
    ) {
      digits = '';
    }


    // Maximum 15 actual digits
    digits =
      digits.substring(
        0,
        15
      );


    const formattedValue =
      this.formatPhoneNumber(
        digits
      );


    if (
      input.value !==
      formattedValue
    ) {
      input.value =
        formattedValue;

      input.dispatchEvent(
        new Event(
          'input',
          {
            bubbles: true
          }
        )
      );
    }
  }


  // =========================================================
  // GET NUMBERS ONLY
  // =========================================================

  private getDigits(
    value: string
  ): string {

    return (
      value
        ?.replace(
          /\D/g,
          ''
        )
      || ''
    );
  }


  // =========================================================
  // FORMAT PHONE NUMBER
  // =========================================================

  private formatPhoneNumber(
    digits: string
  ): string {

    if (
      !digits
    ) {
      return '';
    }


    // Only first digit entered
    if (
      digits.length < 2
    ) {
      return digits;
    }


    const secondDigit =
      digits.charAt(1);


    // =====================================================
    // MOBILE
    // Second digit = 4
    //
    // Example:
    // 0412345678
    // =====================================================

    if (
      secondDigit === '4'
    ) {
      return digits.substring(
        0,
        15
      );
    }


    // =====================================================
    // LANDLINE / OTHER
    //
    // Example:
    // 0112345678
    //
    // Display:
    // (01) 12345678
    //
    // 2 prefix digits + maximum 8 remaining digits
    // =====================================================

    const prefix =
      digits.substring(
        0,
        2
      );

    const remaining =
      digits.substring(
        2,
        15
      );


    if (
      remaining.length === 0
    ) {
      return `(${prefix})`;
    }


    return `(${prefix}) ${remaining}`;
  }


  // =========================================================
  // UPDATE INPUT
  // =========================================================

  private updateInput(
    input: HTMLInputElement,
    digits: string
  ): void {

    const formattedValue =
      this.formatPhoneNumber(
        digits
      );

    input.value =
      formattedValue;


    // Update ngModel
    input.dispatchEvent(
      new Event(
        'input',
        {
          bubbles: true
        }
      )
    );


    // Keep cursor at the end
    setTimeout(
      () => {

        input.setSelectionRange(
          input.value.length,
          input.value.length
        );

      }
    );
  }
}